/**
 * Queues — dead-lettered provider events, and platform deliveries that never landed.
 *
 * "Safe to retry" is printed next to every replay button, deliberately: an operator who is afraid to retry
 * does nothing instead, which is the worse outcome. Replay re-runs an idempotent apply, so replaying
 * something that already landed changes nothing.
 *
 * The one refusal — an event that never verified, so it carries no provider reference — explains WHY and
 * names the alternative, rather than showing a dead button with no story.
 */
import { useState } from "react";
import { api } from "../lib/api";
import { useLoader, useSession } from "../lib/session";
import { dateTimeLong, relative } from "../lib/format";
import { ActionButton, C, card, CardHead, EmptyState, ErrorState, Skeleton } from "../ui/primitives";

export function Queues() {
  const { locked } = useSession();
  const { data, error, loading, reload } = useLoader(() => api.dlq(), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ id: string; text: string; ok: boolean } | null>(null);

  async function replayWebhook(id: string) {
    setBusy(id); setResult(null);
    try {
      const r = await api.replayWebhook(id);
      setResult({ id, ok: r.state === "applied", text: r.state === "applied" ? "Applied. If it had already landed, this changed nothing." : `Still dead-lettered — ${r.failReason ?? "the cause has not been fixed yet"}` });
      reload();
    } catch (e) {
      setResult({ id, ok: false, text: e instanceof Error ? e.message : "Replay failed" });
    } finally { setBusy(null); }
  }
  async function replayOutbox(id: string) {
    setBusy(id); setResult(null);
    try { await api.replayOutbox(id); setResult({ id, ok: true, text: "Back on the queue with a clean slate — the dispatcher owns delivery from here." }); reload(); }
    catch (e) { setResult({ id, ok: false, text: e instanceof Error ? e.message : "Requeue failed" }); }
    finally { setBusy(null); }
  }

  if (loading) return <div style={card}><Skeleton label="Loading queues…" /></div>;
  if (error || !data) return <div style={card}><ErrorState title="Couldn't load the queues." body={error ?? "The engine did not answer."} onRetry={reload} /></div>;

  const empty = data.webhooks.length === 0 && data.outbox.length === 0;
  if (empty) {
    return (
      <div style={card}>
        <EmptyState tone="good" title="Both queues are empty."
          body="Every provider event applied cleanly and every platform delivery went through. This is the system working — not a broken page." />
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(360px,1fr))", gap: 12, alignItems: "start" }}>
      <div style={card}>
        <CardHead title={`Dead-lettered webhooks`} sub="Provider events we could not apply, each with the reason it failed."
          right={data.webhooks.length > 0 ? <Count n={data.webhooks.length} tone="danger" /> : undefined} />
        {data.webhooks.length === 0
          ? <EmptyState tone="good" title="Nothing dead-lettered." body="Every provider event applied cleanly." />
          : data.webhooks.map((w) => {
            // An event that never verified carries a synthetic id and no provider reference — it cannot be
            // replayed into money, and the UI has to say so rather than offering a button that will refuse.
            const unreplayable = w.providerEventId.startsWith("unverified_") || w.providerEventId.startsWith("unnormalized_");
            return (
              <div key={w.id} style={{ padding: "13px 16px", borderBottom: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 7 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="bp-mono" style={{ fontSize: 12, fontWeight: 800 }}>{w.id.slice(0, 8)}</span>
                  <span style={pill}>{w.providerKey}{w.kind ? ` · ${w.kind}` : ""}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--ink2)", lineHeight: 1.5 }}><b>Why it's here:</b> {w.failReason ?? "no reason recorded"}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>
                  First seen {dateTimeLong(w.receivedAt, { tz: true })} · {relative(w.receivedAt)}
                </div>
                {unreplayable ? (
                  <div style={{ padding: "9px 12px", border: "1px dashed var(--muted2)", borderRadius: 10, fontSize: 12, lineHeight: 1.5, color: "var(--ink2)" }}>
                    <b>Cannot be replayed.</b> This event never verified, so it carries no provider reference — replaying would apply something we cannot prove happened. Confirm it in the provider's dashboard and request re-delivery from their end instead.
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <ActionButton small locked={locked} onClick={() => void replayWebhook(w.id)}>
                      {busy === w.id ? "Replaying…" : "Replay event"}
                    </ActionButton>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>Safe to retry — applying twice does nothing.</span>
                  </div>
                )}
                {result?.id === w.id && <Result ok={result.ok} text={result.text} />}
              </div>
            );
          })}
      </div>

      <div style={card}>
        <CardHead title="Parked outbox events" sub="Things we could not deliver to the platform after repeated attempts."
          right={data.outbox.length > 0 ? <Count n={data.outbox.length} tone="warn" /> : undefined} />
        {data.outbox.length === 0
          ? <EmptyState tone="good" title="Nothing parked." body="Every event reached the platform." />
          : data.outbox.map((o) => (
            <div key={o.id} style={{ padding: "13px 16px", borderBottom: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 7 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span className="bp-mono" style={{ fontSize: 12, fontWeight: 800 }}>{o.id.slice(0, 8)}</span>
                <span style={pill}>→ platform · {o.kind}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--ink2)", lineHeight: 1.5 }}>
                <b>Why it's here:</b> the platform did not accept it across {o.attempts} delivery attempts. The wallet is correct — the merchant's own books never heard about it.
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>
                First created {dateTimeLong(o.createdAt, { tz: true })} · {relative(o.createdAt)}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <ActionButton small locked={locked} onClick={() => void replayOutbox(o.id)}>
                  {busy === o.id ? "Requeueing…" : "Replay delivery"}
                </ActionButton>
                <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>Safe to retry — delivery is idempotent on the platform side.</span>
              </div>
              {result?.id === o.id && <Result ok={result.ok} text={result.text} />}
            </div>
          ))}
      </div>
    </div>
  );
}

const Count = ({ n, tone }: { n: number; tone: "danger" | "warn" }) => (
  <span className="bp-mono" style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: tone === "danger" ? C.danger : C.warn, borderRadius: 6, padding: "2px 7px" }}>{n}</span>
);

const Result = ({ ok, text }: { ok: boolean; text: string }) => (
  <div style={{ padding: "9px 12px", border: `1px solid ${ok ? "var(--line2)" : C.dangerLine}`, background: ok ? "var(--accentsoft)" : C.dangerSoft, borderRadius: 10, fontSize: 12, fontWeight: 700, color: "var(--ink2)", lineHeight: 1.5 }}>{text}</div>
);

const pill: React.CSSProperties = { fontSize: 11, fontWeight: 800, color: "var(--muted)", border: "1px solid var(--line2)", borderRadius: 6, padding: "2px 7px" };
